import clsx from 'clsx';
import { useMediaQuery } from 'usehooks-ts';
import { Button } from '@components/forms';
import { useInspectionDetailStore } from '@stores';

export type SettingsSaveState = 'idle' | 'saving' | 'saved';

type Props = {
  dirty: boolean;
  saveState: SettingsSaveState;
  saveFailed?: boolean;
  onSave: () => void;
};

const SAVE_BUTTON_LABEL: Record<SettingsSaveState, string> = {
  idle: 'Save changes',
  saving: 'Saving…',
  saved: 'Saved'
};

// Matches LayoutFooter `h-[44px]` so the sticky save bar sits above it.
const LAYOUT_FOOTER_OFFSET = 'bottom-[44px]';

const SettingsSaveBar = ({
  dirty,
  saveState,
  saveFailed = false,
  onSave
}: Props) => {
  const isMobile = useMediaQuery('(max-width: 720px)');
  const { isSideCollapsed } = useInspectionDetailStore();
  const status = saveFailed
    ? "Couldn't save changes. Try again."
    : saveState === 'saved'
      ? 'All changes saved'
      : dirty
        ? 'Unsaved changes across all tabs'
        : 'No changes';

  return (
    <div
      className={clsx(
        'fixed right-0 z-30 border-t border-grey-200 bg-white/95 backdrop-blur',
        LAYOUT_FOOTER_OFFSET,
        isMobile ? 'left-0' : isSideCollapsed ? 'left-[80px]' : 'left-[256px]'
      )}>
      <div className="mx-auto flex w-full max-w-[760px] items-center justify-between gap-4 px-6 py-3.5">
        <span
          role={saveFailed ? 'alert' : undefined}
          className={clsx(
            'text-sm',
            saveFailed
              ? 'font-medium text-error-light'
              : saveState === 'saved'
                ? 'font-medium text-success-700'
                : dirty
                  ? 'font-medium text-grey-700'
                  : 'font-normal text-grey-500'
          )}>
          {status}
        </span>
        <Button
          type="button"
          disabled={!dirty || saveState !== 'idle'}
          className={clsx(
            'min-w-[170px] !rounded px-10 !text-sm font-semibold !normal-case',
            saveState === 'saved' ? 'bg-success-600' : 'bg-primary'
          )}
          onClick={onSave}>
          {SAVE_BUTTON_LABEL[saveState]}
        </Button>
      </div>
    </div>
  );
};

export default SettingsSaveBar;
